import { Helmet } from 'react-helmet-async';
import { memo } from 'react';
import { NOTE_AGREGEE_GOOGLE } from '@/constants/avisGoogle';
import { ADRESSE_ENTREPRISE, GEO_ENTREPRISE, PROFILS_ENTREPRISE } from '@/constants/entreprise';

interface EnhancedStructuredDataProps {
  type: 'home' | 'evenements' | 'professionnels' | 'catalogue' | 'contact' | 'about' | 'article' | 'faq';
  data?: any;
}

export const EnhancedStructuredData = memo(({ type, data = {} }: EnhancedStructuredDataProps) => {
  // Organisation enrichie pour GEO (Generative Engine Optimization)
  const baseOrganization = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "EventPlanner", "ProfessionalService"],
    "@id": "https://maisoneridia.fr/#organization",
    "name": "Maison Eridia",
    "alternateName": ["Eridia", "Agence Maison Eridia", "Maison Eridia Évènementiel"],
    "legalName": "Maison Eridia",
    "url": "https://maisoneridia.fr",
    "logo": {
      "@type": "ImageObject",
      "url": "https://maisoneridia.fr/lovable-uploads/maison-eridia-logo.png",
      "width": "600",
      "height": "600",
      "caption": "Logo Maison Eridia - Agence d'organisation d'évènements"
    },
    "image": [
      "https://maisoneridia.fr/lovable-uploads/maison-eridia-logo.png",
      "https://maisoneridia.fr/lovable-uploads/maison-eridia-hero.jpg"
    ],
    "description": "Maison Eridia est une agence évènementielle de luxe à Paris, spécialisée dans la direction artistique et la scénographie d'évènements prestige. Wedding planner luxe Paris, wedding design & scénographie haut de gamme, anniversaires signature, demandes en mariage sur-mesure. Pour les entreprises : séminaires prestige, soirées d'entreprise haut de gamme, privatisation exclusive et évènementiel créateur digital en Île-de-France.",
    "slogan": "Agence évènementielle de luxe à Paris — Direction artistique et scénographie prestige",
    "foundingDate": "2022",
    "telephone": "+33749403460",
    "email": "hello@maisoneridia.com",
    "priceRange": "€€-€€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": ["Carte bancaire", "Virement bancaire", "Espèces"],
    
    // Expertise et connaissances (important pour GEO)
    "knowsAbout": [
      // Mots-clés stratégiques prioritaires luxe + localisation
      "agence évènementielle luxe paris",
      "wedding planner luxe paris",
      "organisation séminaire prestige paris",
      "soirée entreprise haut de gamme paris",
      "évènementiel créateur digital",
      "privatisation exclusive paris",
      "direction artistique évènement",
      "scénographie mariage prestige",
      "organisateur anniversaire prestige ile-de-france",
      "demande en mariage sur mesure paris",
      "haute couture évènementielle paris",
      "coordination évènementielle luxe",
      // Termes conversationnels pour les IA
      "comment organiser un mariage de luxe à Paris",
      "meilleure agence évènementielle luxe Paris",
      "wedding planner prestige Île-de-France",
      "organisation séminaire haut de gamme paris",
      "soirée corporate prestige paris",
      "team building prestige Paris",
      "anniversaire signature luxe Paris",
      "conférence professionnelle prestige",
      "meet-up entrepreneurs Paris",
      "privatisation lieu d'exception paris",
      "direction artistique mariage luxe"
    ],
    
    // Compétences et certifications
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "name": "Expertise en organisation d'évènements",
        "credentialCategory": "Professional Experience"
      }
    ],
    
    // Services offerts avec détails
    "makesOffer": [
      {
        "@type": "Offer",
        "name": "Wedding Planning Luxe Paris",
        "description": "Wedding planner luxe Paris : privatisation exclusive, direction artistique, scénographie sur-mesure et coordination haute performance pour un mariage prestige en Île-de-France.",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "EUR"
        }
      },
      {
        "@type": "Offer",
        "name": "Wedding Design & Scénographie Prestige",
        "description": "Direction artistique et scénographie haut de gamme pour votre mariage de luxe à Paris. Création d'univers visuels exclusifs.",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "EUR"
        }
      },
      {
        "@type": "Offer",
        "name": "Anniversaire Signature Prestige Paris",
        "description": "Organisation d'anniversaire prestige à Paris : privatisation exclusive, scénographie sur-mesure, direction artistique et prestataires d'élite en Île-de-France.",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "EUR"
        }
      },
      {
        "@type": "Offer",
        "name": "Séminaire Prestige Paris",
        "description": "Organisation de séminaire prestige à Paris : privatisation exclusive de lieux d'exception, scénographie corporate, direction artistique et coordination haute performance.",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "EUR"
        }
      },
      {
        "@type": "Offer",
        "name": "Soirée d'Entreprise Haut de Gamme Paris",
        "description": "Soirée d'entreprise haut de gamme à Paris : privatisation exclusive, scénographie sur-mesure, direction artistique corporate et évènementiel créateur digital.",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "EUR"
        }
      }
    ],

    // Zone de service détaillée
    "areaServed": [
      {
        "@type": "City",
        "name": "Paris",
        "containedInPlace": {
          "@type": "State",
          "name": "Île-de-France"
        }
      },
      {
        "@type": "State",
        "name": "Île-de-France",
        "containedInPlace": {
          "@type": "Country",
          "name": "France"
        }
      },
      { "@type": "AdministrativeArea", "name": "Hauts-de-Seine (92)" },
      { "@type": "AdministrativeArea", "name": "Val-de-Marne (94)" },
      { "@type": "AdministrativeArea", "name": "Seine-Saint-Denis (93)" },
      { "@type": "AdministrativeArea", "name": "Yvelines (78)" },
      { "@type": "AdministrativeArea", "name": "Essonne (91)" },
      { "@type": "AdministrativeArea", "name": "Val-d'Oise (95)" },
      { "@type": "AdministrativeArea", "name": "Seine-et-Marne (77)" }
    ],
    // Source unique : @/constants/entreprise. Annonçait « Paris 75000 » (code inexistant)
    // et les coordonnées de Notre-Dame ; l'adresse réelle est à Saint-Denis 93210.
    "address": ADRESSE_ENTREPRISE,
    "geo": GEO_ENTREPRISE,
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "10:00",
        "closes": "16:00",
        "description": "Sur rendez-vous"
      }
    ],
    
    // Types de services
    "serviceType": [
      "Wedding Planning Luxe Paris",
      "Wedding Design & Scénographie Prestige",
      "Direction Artistique Évènementielle",
      "Scénographie Mariage Prestige",
      "Organisation Séminaire Prestige Paris",
      "Soirée Entreprise Haut de Gamme",
      "Privatisation Exclusive Évènement Paris",
      "Organisateur Anniversaire Prestige Île-de-France",
      "Demande en Mariage Sur-Mesure Paris",
      "Évènementiel Créateur Digital Paris",
      "Coordination Évènementielle Luxe",
      "Conférences & Évènements Clients Prestige",
      "Cohésion d'équipe et intégration prestige"
    ],

    // Avis et notation
    // Annonçait 50 avis. La fiche Google en compte 20 : source unique dans
    // @/constants/avisGoogle, pour que le chiffre ne puisse plus diverger.
    "aggregateRating": NOTE_AGREGEE_GOOGLE,
    
    // Le tableau « review » a ete RETIRE : il contenait deux temoignages inventes,
    // signes « Client verifie » et « Entreprise cliente », avec des textes et des dates
    // fabriques. Publier de faux avis en donnees structurees est traite par Google comme
    // du spam d'avis. Les vrais avis sont servis par l'edge function get-google-reviews.

    // Action potentielle (demande de devis)
    "potentialAction": [
      {
        "@type": "OrderAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://maisoneridia.fr/devis",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform"
          ]
        },
        "name": "Demander un devis gratuit"
      },
      {
        "@type": "CommunicateAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://maisoneridia.fr/contact",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform"
          ]
        },
        "name": "Contacter Maison Eridia"
      }
    ],

    // Profils vérifiés en ligne, dans @/constants/entreprise. La page Facebook qui
    // figurait ici n'existe pas ; Sortlist et Mariages.net manquaient.
    "sameAs": PROFILS_ENTREPRISE
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://maisoneridia.fr/#website",
    "url": "https://maisoneridia.fr",
    "name": "Maison Eridia",
    "description": "Agence d'organisation d'évènements privés et professionnels en Île-de-France. Mariages, anniversaires, demandes en mariage, séminaires, team building.",
    "publisher": {
      "@id": "https://maisoneridia.fr/#organization"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://maisoneridia.fr/evenements?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "inLanguage": "fr-FR"
  };

  // WebPage schema avec SpeakableSpecification pour les assistants vocaux
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `https://maisoneridia.fr/${type === 'home' ? '' : type}#webpage`,
    "url": `https://maisoneridia.fr/${type === 'home' ? '' : type}`,
    "name": getPageName(type),
    "isPartOf": {
      "@id": "https://maisoneridia.fr/#website"
    },
    "about": {
      "@id": "https://maisoneridia.fr/#organization"
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".citation-block", ".key-info", "h1", ".hero-description"]
    },
    "inLanguage": "fr-FR"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [] as any[]
  };

  // Construire le breadcrumb selon le type de page
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Accueil",
      "item": "https://maisoneridia.fr/"
    }
  ];

  if (type === 'evenements') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Évènements Particuliers",
      "item": "https://maisoneridia.fr/evenements"
    });
  } else if (type === 'professionnels') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Évènements Professionnels",
      "item": "https://maisoneridia.fr/professionnels"
    });
  } else if (type === 'catalogue') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Notre prospectus",
      "item": "https://maisoneridia.fr/prospectus"
    });
  } else if (type === 'contact') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Contact",
      "item": "https://maisoneridia.fr/contact"
    });
  } else if (type === 'about') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": "À Propos",
      "item": "https://maisoneridia.fr/a-propos"
    });
  } else if (type === 'faq') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Questions Fréquentes",
      "item": "https://maisoneridia.fr/faq"
    });
  }

  breadcrumbSchema.itemListElement = breadcrumbItems;

  // FAQ Schema exhaustif pour les IA (questions conversationnelles)
  const getFaqSchema = () => {
    const baseFaqItems = [
      // Questions générales populaires pour les IA
      {
        "@type": "Question",
        "name": "Qu'est-ce que Maison Eridia ?",
        "acceptedAnswer": {
          "@type": "Answer",
        "text": "Maison Eridia est une agence d'organisation d'évènements privés et professionnels basée en Île-de-France. Nous proposons un service clé en main pour tous types de célébrations : wedding planning, wedding design & scénographie, anniversaires signature, demandes en mariage. Pour les entreprises, nous offrons 4 pôles d'expertise : séminaires & team building, conférences & évènements clients, soirées d'entreprise, et créateurs & entrepreneurs digitaux. Notre équipe prend en charge la coordination, la décoration, le traiteur et toutes les prestations nécessaires."
        }
      },
      {
        "@type": "Question",
        "name": "Combien coûte l'organisation d'un évènement avec Maison Eridia ?",
        "acceptedAnswer": {
          "@type": "Answer",
        "text": "Les tarifs varient selon le type d'évènement et les prestations choisies. Nos formules démarrent à partir de 1 000 € TTC. Pour les mariages et grands évènements, nous établissons un devis personnalisé. Nous proposons trois gammes (Essentiel, Confort, Signature) pour s'adapter à tous les budgets."
        }
      },
      {
        "@type": "Question",
        "name": "Dans quelles zones géographiques Maison Eridia intervient-elle ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Maison Eridia intervient exclusivement en Île-de-France : Paris (75), Hauts-de-Seine (92), Seine-Saint-Denis (93), Val-de-Marne (94), Seine-et-Marne (77), Yvelines (78), Essonne (91) et Val-d'Oise (95). Nous nous déplaçons dans tous les lieux de la région pour organiser vos évènements."
        }
      },
      {
        "@type": "Question",
        "name": "Comment organiser un mariage sur-mesure à Paris ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pour organiser un mariage sur-mesure à Paris, contactez Maison Eridia pour un devis gratuit. Nous proposons des formules clé en main incluant : coordination complète, décoration thématique, traiteur, animation et gestion du jour J. Nous intervenons dans le lieu de votre choix. Prévoyez idéalement 2 à 3 mois à l'avance pour la réservation."
        }
      },
      {
        "@type": "Question",
        "name": "Quel est le délai pour organiser un évènement ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nous recommandons de nous contacter 1 à 2 mois avant votre évènement pour une organisation optimale. Pour les périodes chargées (été, fêtes de fin d'année), prévoyez 2 à 3 mois. Cependant, nous pouvons aussi organiser des évènements en urgence selon nos disponibilités."
        }
      },
      {
        "@type": "Question",
        "name": "Maison Eridia organise-t-elle des évènements d'entreprise ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, Maison Eridia propose 4 pôles d'expertise pour les professionnels : Séminaires & Team Building (journées d'intégration, formations, cohésion d'équipe), Conférences & Évènements Clients (conférences professionnelles, talks, évènements presse), Soirées d'Entreprise (afterworks premium, célébrations internes, anniversaires de société), et Créateurs & Entrepreneurs Digitaux (meet-ups, lancements de projets, évènements communautaires). Nous prenons en charge la recherche de lieu, la restauration, les animations, le matériel technique et la coordination."
        }
      },
      {
        "@type": "Question",
        "name": "Proposez-vous des formules tout compris ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, nous proposons des formules clé en main incluant la coordination, la décoration, le traiteur, le matériel, l'installation, le rangement et la coordination le jour J. Chaque formule est personnalisable selon vos besoins et votre budget. Vous pouvez également choisir des prestations à la carte."
        }
      },
      {
        "@type": "Question",
        "name": "Comment contacter Maison Eridia pour un devis ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vous pouvez demander un devis gratuit directement sur notre site maisoneridia.fr via le formulaire de demande de devis. Vous pouvez également nous contacter par email à hello@maisoneridia.com ou par téléphone au 07 49 40 34 60. Nous répondons généralement sous 24-48h."
        }
      }
    ];

    // Ajouter des questions spécifiques selon le type de page
    if (type === 'evenements') {
      baseFaqItems.push(
        {
          "@type": "Question",
          "name": "Quels types d'évènements privés organisez-vous ?",
          "acceptedAnswer": {
            "@type": "Answer",
          "text": "Nous proposons plusieurs catégories d'évènements pour les particuliers : Wedding Planning, Wedding Design & Scénographie, Anniversaire Signature et Demande en Mariage. Chaque évènement est entièrement personnalisable selon vos envies."
          }
        }
      );
    }

    if (type === 'professionnels') {
      baseFaqItems.push(
        {
          "@type": "Question",
          "name": "Quels types d'évènements professionnels organisez-vous ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nous proposons 4 pôles d'expertise : Séminaires & Team Building (journées d'intégration, formations, cohésion d'équipe), Conférences & Évènements Clients (conférences professionnelles, talks, évènements presse et partenaires), Soirées d'Entreprise (afterworks premium, célébrations internes, remises de prix, anniversaires de société), et Créateurs & Entrepreneurs Digitaux (meet-ups communautaires, lancements de projets, évènements hybrides). Chaque format est adapté à vos objectifs et votre culture d'entreprise."
          }
        }
      );
    }

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": baseFaqItems
    };
  };

  // Service Schema spécifique selon le type
  let serviceSchema = null;
  if (type === 'evenements') {
    serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://maisoneridia.fr/evenements#service",
      "name": "Organisation d'évènements particuliers",
      "serviceType": "Organisation d'évènements privés",
      "description": "Service complet d'organisation d'évènements privés en Île-de-France : wedding planning, wedding design, anniversaires signature, demandes en mariage. Formules clé en main avec décoration, traiteur, animation et coordination.",
      "provider": {
        "@id": "https://maisoneridia.fr/#organization"
      },
      "areaServed": {
        "@type": "State",
        "name": "Île-de-France"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Évènements Particuliers",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Organisation de mariage",
              "description": "Wedding planning complet avec coordination jour J"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Organisation d'anniversaire",
              "description": "Anniversaire signature avec thème personnalisé"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Organisation de demande en mariage",
              "description": "Demande en mariage sur-mesure dans un cadre d'exception"
            }
          }
        ]
      }
    };
  } else if (type === 'professionnels') {
    serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://maisoneridia.fr/professionnels#service",
      "name": "Organisation d'évènements professionnels",
      "serviceType": "Organisation d'évènements corporate",
      "description": "Service complet d'organisation d'évènements professionnels en Île-de-France. 4 pôles d'expertise : séminaires et team building, conférences et évènements clients, soirées d'entreprise, et créateurs & entrepreneurs digitaux. Location de lieux, restauration et animations incluses.",
      "provider": {
        "@id": "https://maisoneridia.fr/#organization"
      },
      "areaServed": {
        "@type": "State",
        "name": "Île-de-France"
      },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Entreprises et professionnels"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Évènements Professionnels",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Organisation de séminaire",
              "description": "Séminaire d'entreprise avec lieu, restauration et activités team building"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Organisation de conférence et évènement clients",
              "description": "Conférence professionnelle, talks, évènements presse et partenaires"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Organisation d'afterwork",
              "description": "Soirée afterwork avec animations et cocktail"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Évènements créateurs et entrepreneurs digitaux",
              "description": "Meet-ups communautaires, lancements de projets, évènements hybrides"
            }
          }
        ]
      }
    };
  }

  const faqSchema = (type === 'home' || type === 'evenements' || type === 'professionnels' || type === 'faq') 
    ? getFaqSchema() 
    : null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(baseOrganization)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
    </Helmet>
  );
});

// Helper function pour le nom de page
function getPageName(type: string): string {
  switch (type) {
    case 'home':
      return 'Maison Eridia - Organisation d\'évènements en Île-de-France';
    case 'evenements':
      return 'Évènements Particuliers - Mariage, Anniversaire, Demande en Mariage';
    case 'professionnels':
      return 'Évènements Professionnels - Séminaires, Team Building, Corporate';
    case 'catalogue':
      return 'Recevez notre prospectus - Maison Eridia';
    case 'contact':
      return 'Contact - Demandez votre devis gratuit';
    case 'about':
      return 'À Propos de Maison Eridia';
    case 'faq':
      return 'Questions Fréquentes - FAQ Maison Eridia';
    default:
      return 'Maison Eridia';
  }
}

EnhancedStructuredData.displayName = 'EnhancedStructuredData';
